/**
 * Convert a string to a URL/filename-safe slug
 * Removes invalid filesystem characters and replaces spaces with underscores
 * @param str - The string to slugify
 * @returns Slugified string safe for use in filenames
 * @example slugifyString('John Doe') => 'John_Doe'
 * @example slugifyString('file<>:name') => 'filename'
 */
export const slugifyString = (str: string): string => {
  return str
    .trim() // Trim whitespace first
    .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filesystem characters
    .replace(/\s+/g, '_'); // Replace spaces with underscores
};

/**
 * Format a field name by converting snake_case to Title Case
 * @param fieldName - The field name to format (e.g., 'payor_name')
 * @returns Formatted field name (e.g., 'Payor Name')
 */
export const formatFieldName = (fieldName: string): string => {
  return fieldName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Get initials from a name (first and last name)
 * @param name - Full name (e.g., 'John Doe')
 * @returns Initials (e.g., 'JD')
 */
export const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) {
    return '';
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Avatar color combinations - using full class names for Tailwind JIT compatibility
// Each color has 2 shade variations for more variety
const AVATAR_COLORS = [
  // Orange shades
  'bg-orange-100 text-orange-600 border-orange-600',
  'bg-orange-200 text-orange-700 border-orange-700',
  // Amber shades
  'bg-amber-100 text-amber-600 border-amber-600',
  'bg-amber-200 text-amber-700 border-amber-700',
  // Yellow shades
  'bg-yellow-100 text-yellow-600 border-yellow-600',
  'bg-yellow-200 text-yellow-700 border-yellow-700',
  // Lime shades
  'bg-lime-100 text-lime-600 border-lime-600',
  'bg-lime-200 text-lime-700 border-lime-700',
  // Green shades
  'bg-green-100 text-green-600 border-green-600',
  'bg-green-200 text-green-700 border-green-700',
  // Emerald shades
  'bg-emerald-100 text-emerald-600 border-emerald-600',
  'bg-emerald-200 text-emerald-700 border-emerald-700',
  // Teal shades
  'bg-teal-100 text-teal-600 border-teal-600',
  'bg-teal-200 text-teal-700 border-teal-700',
  // Cyan shades
  'bg-cyan-100 text-cyan-600 border-cyan-600',
  'bg-cyan-200 text-cyan-700 border-cyan-700',
  // Sky shades
  'bg-sky-100 text-sky-600 border-sky-600',
  'bg-sky-200 text-sky-700 border-sky-700',
  // Blue shades
  'bg-blue-100 text-blue-600 border-blue-600',
  'bg-blue-200 text-blue-700 border-blue-700',
  // Indigo shades
  'bg-indigo-100 text-indigo-600 border-indigo-600',
  'bg-indigo-200 text-indigo-700 border-indigo-700',
  // Violet shades
  'bg-violet-100 text-violet-600 border-violet-600',
  'bg-violet-200 text-violet-700 border-violet-700',
  // Purple shades
  'bg-purple-100 text-purple-600 border-purple-600',
  'bg-purple-200 text-purple-700 border-purple-700',
  // Fuchsia shades
  'bg-fuchsia-100 text-fuchsia-600 border-fuchsia-600',
  'bg-fuchsia-200 text-fuchsia-700 border-fuchsia-700',
];

/**
 * Get a consistent avatar color based on a name
 * @param name - The name to generate color for
 * @returns Tailwind CSS classes for background, text, and border color
 */
export const getAvatarColor = (name: string): string => {
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};
